'use client';

import React, { useState } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis
} from 'recharts';
import { Target, Award, AlertTriangle, Activity } from 'lucide-react';
import {
  MOCK_TREND_DATA, MOCK_FLOCK_SUMMARIES
} from '../../components/eggProductionData';
import Icon from '@/components/ui/AppIcon';
import { useRole } from '@/lib/useRole';
import Link from 'next/link';
import { Shield } from 'lucide-react';


const COLORS = ['#16a34a', '#22c55e', '#86efac', '#ef4444', '#f97316', '#8b5cf6'];

const QUALITY_DATA = [
  { name: 'Good Eggs', value: 4850, color: '#16a34a' },
  { name: 'Cracked', value: 83, color: '#f97316' },
  { name: 'Dirty', value: 63, color: '#eab308' },
  { name: 'Rejected', value: 142, color: '#ef4444' },
  { name: 'Hatching', value: 1650, color: '#8b5cf6' },
];

const MONTHLY_DATA = [
  { month: 'Apr', eggs: 148200, target: 155000 },
  { month: 'May', eggs: 152400, target: 155000 },
  { month: 'Jun', eggs: 149800, target: 158000 },
  { month: 'Jul', eggs: 155600, target: 160000 },
  { month: 'Aug', eggs: 158900, target: 162000 },
  { month: 'Sep', eggs: 35800, target: 162000 },
];

const PERFORMANCE_DATA = MOCK_FLOCK_SUMMARIES.map(f => ({
  flock: f.flockNumber,
  actual: f.henDayPct,
  target: f.targetPct,
  variance: f.variance,
}));

const RADAR_DATA = [
  { metric: 'HDP %', LY001: 91.75, LY002: 79.29, BRD001: 78.45 },
  { metric: 'Good Egg %', LY001: 95.16, LY002: 94.59, BRD001: 89.19 },
  { metric: 'Crack Rate', LY001: 98.44, LY002: 98.29, BRD001: 97.03 },
  { metric: 'Reject Rate', LY001: 97.92, LY002: 96.31, BRD001: 94.32 },
  { metric: 'Eggs/Bird', LY001: 92, LY002: 79, BRD001: 78 },
];

const ANALYTICS_TABS = ['Production Trends', 'Actual vs Target', 'Quality Analysis', 'Flock Performance'];

export default function EggAnalyticsContent() {
  const [activeTab, setActiveTab] = useState(ANALYTICS_TABS[0]);
  const [farmFilter, setFarmFilter] = useState('all');
  const { can } = useRole();

  if (!can('egg_production.view')) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center">
          <Shield size={28} className="text-red-400" />
        </div>
        <h2 className="text-lg font-bold text-gray-700">Access Denied</h2>
        <p className="text-sm text-gray-400">You do not have permission to view egg production analytics.</p>
        <Link href="/egg-production" className="px-4 py-2 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition-colors">
          Back to Overview
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Production Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Deep analysis of egg production performance, quality trends, and flock comparisons.</p>
        </div>
        <select value={farmFilter} onChange={e => setFarmFilter(e.target.value)} className="text-sm border border-gray-200 rounded-xl px-3 py-2 text-gray-600 bg-white focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="all">All Farms</option>
          <option value="idudi">Idudi Poultry Farm</option>
          <option value="kampala">Kampala Demo Farm</option>
        </select>
      </div>

      {/* Analytics KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Avg Daily Production', value: '5,493', sub: 'Last 7 days', icon: Activity, bg: 'bg-green-100', color: 'text-green-600' },
          { label: 'Avg Hen-Day %', value: '89.4%', sub: 'Farm average', icon: Target, bg: 'bg-blue-100', color: 'text-blue-600' },
          { label: 'Best Performing Flock', value: 'LY-2026-001', sub: '91.75% HDP', icon: Award, bg: 'bg-amber-100', color: 'text-amber-600' },
          { label: 'Needs Attention', value: 'LOCAL-2026-001', sub: '41.99% HDP', icon: AlertTriangle, bg: 'bg-red-100', color: 'text-red-500' },
        ].map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${c.bg}`}>
                <Icon size={18} className={c.color} />
              </div>
              <div>
                <p className="text-xs text-gray-500 font-medium">{c.label}</p>
                <p className="text-base font-bold text-gray-900 mt-0.5">{c.value}</p>
                <p className="text-xs text-gray-400">{c.sub}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit flex-wrap">
        {ANALYTICS_TABS.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${activeTab === tab ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Production Trends */}
      {activeTab === 'Production Trends' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">Daily Egg Production (Last 7 Days)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={MOCK_TREND_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="totalEggs" stroke="#16a34a" strokeWidth={2.5} name="Total Eggs" dot={{ r: 4 }} />
                <Line type="monotone" dataKey="goodEggs" stroke="#22c55e" strokeWidth={2} name="Good Eggs" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="hatchingEggs" stroke="#8b5cf6" strokeWidth={2} name="Hatching" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">Monthly Production vs Target</h2>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={MONTHLY_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v: number) => v.toLocaleString()} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="eggs" name="Actual" fill="#16a34a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="target" name="Target" fill="#bbf7d0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Actual vs Target */}
      {activeTab === 'Actual vs Target' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">Hen-Day Production: Actual vs Target by Flock</h2>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={PERFORMANCE_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="flock" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9ca3af' }} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v: number) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="actual" name="Actual HDP %" fill="#16a34a" radius={[4, 4, 0, 0]} />
                <Bar dataKey="target" name="Target HDP %" fill="#bbf7d0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <h2 className="text-base font-bold text-gray-900">Performance Summary</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {['Flock', 'Breed', 'Age (wk)', 'Actual HDP %', 'Target HDP %', 'Variance', 'Performance'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {MOCK_FLOCK_SUMMARIES.map((f, i) => (
                    <tr key={f.flockId} className={`border-b border-gray-50 hover:bg-gray-50 ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-900">{f.flockNumber}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{f.breed}</td>
                      <td className="px-4 py-3 text-xs text-gray-600">{f.ageWeeks}</td>
                      <td className="px-4 py-3 text-xs font-bold text-gray-900">{f.henDayPct}%</td>
                      <td className="px-4 py-3 text-xs text-gray-500">{f.targetPct}%</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold ${f.variance >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {f.variance >= 0 ? '+' : ''}{f.variance}%
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                          f.status === 'excellent' ? 'bg-green-100 text-green-700' :
                          f.status === 'good' ? 'bg-blue-100 text-blue-700' :
                          f.status === 'below_target'? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {f.status === 'below_target' ? 'Below Target' : f.status.charAt(0).toUpperCase() + f.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Quality Analysis */}
      {activeTab === 'Quality Analysis' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">Egg Quality Distribution (Today)</h2>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={QUALITY_DATA} cx="50%" cy="50%" outerRadius={100} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`} labelLine={false}>
                  {QUALITY_DATA.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v: number) => v.toLocaleString()} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">Crack & Rejection Rate Trend</h2>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={MOCK_TREND_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="crackedEggs" stroke="#f97316" strokeWidth={2} name="Cracked" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="rejectedEggs" stroke="#ef4444" strokeWidth={2} name="Rejected" dot={{ r: 3 }} />
                <Line type="monotone" dataKey="dirtyEggs" stroke="#eab308" strokeWidth={2} name="Dirty" dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Flock Performance */}
      {activeTab === 'Flock Performance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">Flock Performance Radar</h2>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={RADAR_DATA}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                <Radar name="LY-2026-001" dataKey="LY001" stroke="#16a34a" fill="#16a34a" fillOpacity={0.2} />
                <Radar name="LY-2026-002" dataKey="LY002" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                <Radar name="BRD-2026-001" dataKey="BRD001" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.2} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">Weekly Production by Flock</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={MOCK_FLOCK_SUMMARIES.map(f => ({ name: f.flockNumber, weekly: f.eggsThisWeek }))}
                margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v: number) => v.toLocaleString()} />
                <Bar dataKey="weekly" name="Eggs This Week" fill="#16a34a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
